import {
  CheckCircle,
  Error,
  Help,
  Info,
  SvgIconComponent,
  Warning,
} from '@mui/icons-material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { FC, ReactNode, memo, useState } from 'react';
import { getColor } from '../assets/theme';

export interface DialogInterface extends DialogProps {
  onConfirm?: () => Promise<void>;
  title?: string;
  desc?: string | ReactNode;
  icon?: SvgIconComponent;
  dialogStatus?: 'success' | 'warning' | 'error' | 'info' | 'default';
  iconColor?: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary';
  hideIcon?: boolean;
  onClose?: () => void;
  cancelButtonTitle?: string;
  confirmButtonTitle?: string;
  switchButton?: boolean;
  isUseForm?: boolean;
  loading?: boolean;
  disabledLoading?: boolean;
  backBtn?: ReactNode | null;
  additionalBtn?: ReactNode | null;
  disabledConfirmBtn?: boolean;
  backBtnProps?: ButtonProps;
  confirmBtnProps?: LoadingButtonProps;
}

/**
 * A reusable confirmation dialog component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {() => Promise<void>} props.onConfirm - Function to call when confirming
 * @param {string} [props.title] - The dialog title
 * @param {string | ReactNode} [props.desc] - The dialog description
 * @param {SvgIconComponent} [props.icon] - Custom icon component
 * @param {boolean} [props.hideIcon] - Whether to hide the icon
 * @param {() => void} [props.onClose] - Function to call when closing
 * @param {string} [props.iconColor] - Custom icon color
 * @param {string} [props.cancelButtonTitle] - Custom cancel button text
 * @param {string} [props.confirmButtonTitle] - Custom confirm button text
 * @param {boolean} [props.switchButton] - Whether to switch button positions
 * @param {boolean} [props.isUseForm] - Whether the dialog is used within a form
 * @param {boolean} [props.loading] - Whether to show loading state
 * @param {boolean} [props.disabledLoading] - Whether to disable loading state
 * @param {ReactNode | null} [props.backBtn] - Custom back button
 * @param {boolean} [props.disabledConfirmBtn] - Whether to disable confirm button
 */
export const ConfirmationDialog: FC<DialogInterface> = memo(
  ({
    open,
    onConfirm,
    title,
    desc,
    icon,
    hideIcon = false,
    onClose,
    iconColor,
    cancelButtonTitle,
    confirmButtonTitle,
    switchButton,
    isUseForm = false,
    loading = false,
    backBtn,
    disabledLoading = false,
    dialogStatus,
    disabledConfirmBtn = false,
    backBtnProps,
    confirmBtnProps,
    additionalBtn,
    ...props
  }) => {
    const [isLoading, setIsLoading] = useState(false);

    return (
      <Dialog
        {...props}
        open={open}
        sx={{
          '.MuiPaper-root': {
            overflowY: 'unset',
            minWidth: '300px',
            maxWidth: '560px',
          },
        }}
        maxWidth='xl'
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onConfirm?.();
          }
        }}
      >
        {!hideIcon && (
          <Box
            sx={{
              position: 'absolute',
              top: '-36px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 999,
            }}
          >
            <Stack
              sx={{
                width: '79px',
                height: '79px',
                bgcolor: getColor().white,
                borderRadius: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <SvgIcon
                component={
                  icon
                    ? icon
                    : dialogStatus === 'info'
                      ? Info
                      : dialogStatus === 'warning'
                        ? Warning
                        : dialogStatus === 'error'
                          ? Error
                          : dialogStatus === 'success'
                            ? CheckCircle
                            : Help
                }
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: `${iconColor ? getColor()[iconColor] : getColor().primary}15`,
                  p: '12px',
                  borderRadius: '100%',
                  color: iconColor ? getColor()[iconColor] : getColor().primary,
                }}
              />
            </Stack>
          </Box>
        )}
        <Stack
          sx={{
            bgcolor: getColor().white,
            borderRadius: '4px',
            p: '24px 24px 16px 24px',
            justifyContent: 'flex-start',
            fontWeight: 600,
          }}
        >
          <DialogTitle
            sx={{
              p: '16px 0',
              fontWeight: 600,
              color: iconColor ? getColor()[iconColor] : 'inherit',
              textAlign: 'center',
            }}
            className='dialog-title'
          >
            {title || 'Confirmation'}
          </DialogTitle>
          <DialogContent
            sx={{ p: '8px 0!important' }}
            className='dialog-content'
          >
            {!desc && (
              <Typography sx={{ pb: '24px' }}>
                Are you sure you want to proceed? This action cannot be undone.
              </Typography>
            )}
            {typeof desc == 'string' ? (
              <Typography sx={{ pb: '24px' }}>{desc}</Typography>
            ) : (
              <Box
                sx={{ pb: isUseForm ? '' : '16px' }}
                className='dialog-content_wrapper'
              >
                {desc}
              </Box>
            )}
          </DialogContent>
          {!isUseForm && (
            <DialogActions sx={{ p: '0 0 8px 0' }} className='dialog-action'>
              <Stack direction={switchButton ? 'row-reverse' : 'row'} gap='8px'>
                {additionalBtn}
                {backBtn || (
                  <Button
                    variant='text'
                    onClick={() => {
                      onClose && onClose();
                    }}
                    disabled={loading || isLoading}
                    {...backBtnProps}
                  >
                    {cancelButtonTitle || 'Close'}
                  </Button>
                )}
                {!!onConfirm && (
                  <LoadingButton
                    variant='contained'
                    type='submit'
                    onClick={async () => {
                      try {
                        !disabledLoading && setIsLoading(true);
                        await onConfirm();
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    loading={loading || isLoading}
                    disabled={disabledConfirmBtn}
                    {...confirmBtnProps}
                  >
                    {confirmButtonTitle || 'Confirm'}
                  </LoadingButton>
                )}
              </Stack>
            </DialogActions>
          )}
        </Stack>
      </Dialog>
    );
  },
);

/**
 * Custom hook for managing confirmation dialogs
 * @returns {Object} An object containing dialog-related functions and components
 */
const useConfirmationDialog = () => {
  const [showDialog, setShowDialog] = useState(false);

  /**
   * Shows the confirmation dialog
   */
  const onShowDialog = () => {
    setShowDialog(true);
  };

  /**
   * Closes the confirmation dialog
   */
  const onCloseDialog = () => {
    setShowDialog(false);
  };

  /**
   * A memoized dialog component
   * @param {Object} props - The component props
   * @param {() => Promise<void>} props.onConfirm - Function to call when confirming
   * @param {string} [props.title] - The dialog title
   * @param {string | ReactNode} [props.desc] - The dialog description
   * @param {SvgIconComponent} [props.icon] - Custom icon component
   * @param {boolean} [props.hideIcon] - Whether to hide the icon
   * @param {() => void} [props.onClose] - Function to call when closing
   */

  return {
    showDialog,
    onShowDialog,
    onCloseDialog,
    DialogComponentV2: ConfirmationDialog,
  };
};

export default useConfirmationDialog;
